import React from 'react';
import { InvestmentArea } from 'screens/Investors/Wizard/steps/InvestmentArea';
import { Area } from './Wizard';
import { ShowAt } from 'consts/breakpoints';
import { SpacingBlock } from 'components/spacing-block';
import { ImportantArea } from './steps/ImportantArea';
import { BudgetArea } from './steps/BudgetArea';
import { OtherApartmentArea } from './steps/OtherApartment';
import { IInvestorPreferences } from 'store/sagas/apiService/types';
import { isNil, noop } from 'lodash';
import { useLocale } from 'locale';


interface IStepsContentProps {
  activeArea?: Area;
  fullActive?: boolean;
  savedPreferences?: IInvestorPreferences;
  savedAreas?: Set<Area>;
  btnText?: string;
  submitWizard?: () => void;
  setActiveArea: (area: Area) => void;
  setSavedAreas: (value: React.SetStateAction<Set<Area>>) => void;
  onUpdatePreferences: (data: any) => void;
}

export const StepsContent: React.FC<IStepsContentProps> = ({
  activeArea,
  fullActive,
  savedAreas,
  savedPreferences,
  submitWizard,
  setActiveArea,
  setSavedAreas,
  onUpdatePreferences,
  btnText,
}) => {
  const { t } = useLocale();
  const investmentTitle = t('investors.investment.title');
  const importantTitle = t('investors.important.title');
  const budgetTitle = t('investors.budget.title');
  const budgetSubtitle = t('investors.budget.subtitle');
  const otherApartmentTitle = t('investors.otherApartment.title');
  const otherApartmentSubtitle = t('investors.otherApartment.subtitle');

  const { places, goal, budget, hasProperty } = savedPreferences || {};
  const savedPlaces = isNil(places)
      ? null
      : places.map(el => ({ docId: el.docId, name: el.ref.name }));

  return (
    <>
      <InvestmentArea
        title={investmentTitle}
        isActive={fullActive || activeArea === Area.Investment}
        isSaved={fullActive || savedAreas.has(Area.Investment)}
        data={savedPlaces ? savedPlaces.map(el => el.docId) : null}
        setArea={fullActive ? noop : () => {
          setActiveArea(Area.Important);
          setSavedAreas(prev => {
            const newSet = new Set(prev);
            newSet.add(Area.Investment);
            return newSet;
          });
        }}
        onUpdate={onUpdatePreferences}
        selfClick={() => setActiveArea(Area.Investment)}
        btnText={btnText}
        fullActive={fullActive}
      />
      <ShowAt to={2}>
        <SpacingBlock mTop={1.5} />
      </ShowAt>
      <ShowAt from={3}>
        <SpacingBlock mTop={2} />
      </ShowAt>
      <ImportantArea
        title={importantTitle}
        isActive={fullActive || activeArea === Area.Important}
        isSaved={fullActive || savedAreas.has(Area.Important)}
        data={goal}
        setArea={fullActive ? noop : () => {
          setActiveArea(Area.Budget);
          setSavedAreas(prev => {
            const newSet = new Set(prev);
            newSet.add(Area.Important);
            return newSet;
          });
        }}
        onUpdate={onUpdatePreferences}
        selfClick={() => setActiveArea(Area.Important)}
        btnText={btnText}
        fullActive={fullActive}
      />
      <ShowAt to={2}>
        <SpacingBlock mTop={1.5} />
      </ShowAt>
      <ShowAt from={3}>
        <SpacingBlock mTop={2} />
      </ShowAt>
      <BudgetArea
        title={budgetTitle}
        text={budgetSubtitle}
        isActive={fullActive || activeArea === Area.Budget}
        isSaved={fullActive || savedAreas.has(Area.Budget)}
        data={budget}
        setArea={fullActive ? noop : () => {
          setActiveArea(Area.AnotherApartment);
          setSavedAreas(prev => {
            const newSet = new Set(prev);
            newSet.add(Area.Budget);
            return newSet;
          });
        }}
        onUpdate={onUpdatePreferences}
        selfClick={() => setActiveArea(Area.Budget)}
        btnText={btnText}
        fullActive={fullActive}
      />
      <ShowAt to={2}>
        <SpacingBlock mTop={1.5} />
      </ShowAt>
      <ShowAt from={3}>
        <SpacingBlock mTop={2} />
      </ShowAt>
      <OtherApartmentArea
        title={otherApartmentTitle}
        text={otherApartmentSubtitle}
        isActive={fullActive || activeArea === Area.AnotherApartment}
        isSaved={fullActive || savedAreas.has(Area.AnotherApartment)}
        data={hasProperty}
        setArea={fullActive ? noop : () => {
          submitWizard();
          setSavedAreas(prev => {
            const newSet = new Set(prev);
            newSet.add(Area.AnotherApartment);
            return newSet;
          });
        }}
        onUpdate={onUpdatePreferences}
        selfClick={() => setActiveArea(Area.AnotherApartment)}
        btnText={btnText}
        fullActive={fullActive}
      />
      <SpacingBlock style={{ overflow: 'hidden' }} mTop={7} />
    </>
  );
};
